"use strict";
/**
 * APITable <https://github.com/apitable/apitable>
 * Copyright (C) 2022 APITable Ltd. <https://apitable.com>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.FormulaExprParser = void 0;
const token_1 = require("../lexer/token");
const ast_1 = require("./ast");
const functions_1 = require("../../formula_parser/functions");
const i18n_1 = require("../../exports/i18n");
const self_ref_error_1 = require("../../formula_parser/errors/self_ref.error");
/**
  * operator precedence
  * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Operator_Precedence
  * In the array below, the priority is sorted from high to low. The same line has the same priority.
  */
const PriorityMap = new Map();
[
    [token_1.TokenType.Times, token_1.TokenType.Div, token_1.TokenType.Mod],
    [token_1.TokenType.Add, token_1.TokenType.Minus],
    [token_1.TokenType.Greater, token_1.TokenType.GreaterEqual, token_1.TokenType.Less, token_1.TokenType.LessEqual],
    [token_1.TokenType.Equal, token_1.TokenType.NotEqual],
    [token_1.TokenType.And],
    [token_1.TokenType.Or],
    [token_1.TokenType.Concat],
].forEach((arr, index) => arr.forEach(type => PriorityMap.set(type, index)));
class FormulaExprParser {
    constructor(lexer, context) {
        this.lexer = lexer;
        this.context = context;
        this.currentToken = this.lexer.getNextToken();
    }
    parse() {
        const node = this.expr();
        if (this.currentToken != null) {
            console.error(this.currentToken);
            throw new Error((0, i18n_1.t)(i18n_1.Strings.function_err_unrecognized_char, {
                value: this.currentToken.value,
            }));
        }
        return node;
    }
    next(type) {
        if (this.currentToken == null) {
            return null;
        }
        if (this.currentToken.type === type) {
            this.currentToken = this.lexer.getNextToken();
        }
        else {
            switch (type) {
                case token_1.TokenType.LeftParen:
                    throw new SyntaxError((0, i18n_1.t)(i18n_1.Strings.function_err_no_left_bracket));
                case token_1.TokenType.RightParen:
                    throw new SyntaxError((0, i18n_1.t)(i18n_1.Strings.function_err_end_of_right_bracket));
            }
            throw new SyntaxError((0, i18n_1.t)(i18n_1.Strings.function_err_unable_parse_char, {
                value: this.currentToken.value,
            }));
        }
        return this.currentToken;
    }
    factor() {
        // factor : VALUE | LEFT_PAREN expr RIGHT_PAREN | NOT expr
        const token = this.currentToken;
        if (!token) {
            throw new Error((0, i18n_1.t)(i18n_1.Strings.function_err_wrong_function_suffix));
        }
        switch (token.type) {
            // field variable: {value}
            case token_1.TokenType.Value: {
                this.next(token_1.TokenType.Value);
                const tokenValue = token.value.slice(1, -1);
                if (tokenValue === this.context.field.id) {
                    throw new self_ref_error_1.SelfRefError();
                }
                return new ast_1.ValueOperandNode(token, this.context);
            }
            // field variable: value (without curly braces)
            case token_1.TokenType.PureValue: {
                this.next(token_1.TokenType.PureValue);
                const tokenValue = token.value;
                if (tokenValue === this.context.field.id) {
                    throw new self_ref_error_1.SelfRefError();
                }
                return new ast_1.PureValueOperandNode(token, this.context, this.context.field);
            }
            // Preset functions: Sum/Average ...
            case token_1.TokenType.Call: {
                this.next(token_1.TokenType.Call);
                const node = new ast_1.CallOperandNode(token);
                const FuncClass = functions_1.Functions.get(node.value.toUpperCase());
                if (!FuncClass) {
                    throw new TypeError((0, i18n_1.t)(i18n_1.Strings.function_err_not_definition, {
                        name: node.value,
                    }));
                }
                this.next(token_1.TokenType.LeftParen);
                if (!this.currentToken) {
                    throw new Error((0, i18n_1.t)(i18n_1.Strings.function_err_end_of_right_bracket));
                }
                while (this.currentToken.type !== token_1.TokenType.RightParen) {
                    node.params.push(this.expr());
                    if (!this.currentToken) {
                        throw new Error((0, i18n_1.t)(i18n_1.Strings.function_err_end_of_right_bracket));
                    }
                    // Exclude multiple parameters without commas
                    if (this.currentToken.type !== token_1.TokenType.Comma) {
                        break;
                    }
                }
                const valueType = FuncClass.func.getReturnType(node.params);
                node.valueType = valueType;
                this.next(token_1.TokenType.RightParen);
                return node;
            }
            // number: 123.333
            case token_1.TokenType.Number: {
                this.next(token_1.TokenType.Number);
                return new ast_1.NumberOperandNode(token);
            }
            // string: 'xyz'
            case token_1.TokenType.String: {
                this.next(token_1.TokenType.String);
                return new ast_1.StringOperandNode(token);
            }
            // Left parenthesis: '('
            case token_1.TokenType.LeftParen: {
                this.next(token_1.TokenType.LeftParen);
                const node = this.expr();
                this.next(token_1.TokenType.RightParen);
                return node;
            }
            // Negate sign (unary arithmetic sign): '!'
            case token_1.TokenType.Not: {
                this.next(token_1.TokenType.Not);
                const node = this.factor();
                return new ast_1.UnaryOperatorNode(node, token);
            }
            // + sign (unary arithmetic sign): '+'
            case token_1.TokenType.Add: {
                this.next(token_1.TokenType.Add);
                const node = this.factor();
                return new ast_1.UnaryOperatorNode(node, token);
            }
            // -sign (unary arithmetic sign): '-'
            case token_1.TokenType.Minus: {
                this.next(token_1.TokenType.Minus);
                const node = this.factor();
                return new ast_1.UnaryOperatorNode(node, token);
            }
            case token_1.TokenType.Comma: {
                this.next(token_1.TokenType.Comma);
                const node = this.expr();
                return node;
            }
            case token_1.TokenType.Blank: {
                this.next(token_1.TokenType.Blank);
                return this.factor();
            }
            default:
                throw new Error((0, i18n_1.t)(i18n_1.Strings.function_err_unknown_operator, {
                    type: token.value,
                }));
        }
    }
    expr(inner) {
        // expr   : factor ((&& | ||) factor)*
        // factor : Number | String | Call | VALUE | LEFT_PAREN expr RIGHT_PAREN | NOT expr
        let node = this.factor();
        while (this.currentToken &&
            [
                token_1.TokenType.And, token_1.TokenType.Or, token_1.TokenType.Add, token_1.TokenType.Times, token_1.TokenType.Div, token_1.TokenType.Minus,
                token_1.TokenType.Mod, token_1.TokenType.Concat, token_1.TokenType.Equal, token_1.TokenType.NotEqual, token_1.TokenType.Greater, token_1.TokenType.GreaterEqual,
                token_1.TokenType.Less, token_1.TokenType.LessEqual,
            ].includes(this.currentToken.type)) {
            const token = this.currentToken;
            this.next(token.type);
            if (!this.currentToken) {
                throw new Error((0, i18n_1.t)(i18n_1.Strings.function_err_wrong_function_suffix));
            }
            let right;
            let nextToken = null;
            const currentToken = this.currentToken;
            const currentTokenIndex = this.lexer.currentTokenIndex;
            /**
              * Take a step forward, get the token and go back
              *
              * 1. If you encounter a function or left parenthesis, go forward to test the entire function or parenthesis content,
              * get the following operator and then fall back
              *
              * 2. If it is not a function, just try a token forward, get the operator and then fall back
              */
            if ([token_1.TokenType.Call, token_1.TokenType.LeftParen].includes(currentToken.type)) {
                this.factor();
                nextToken = this.currentToken;
                this.currentToken = currentToken;
                this.lexer.currentTokenIndex = currentTokenIndex;
            }
            else {
                nextToken = this.lexer.getNextToken();
                this.lexer.getPrevToken();
            }
            const currentOpIndex = PriorityMap.get(token.type);
            if (nextToken) {
                const nextOpIndex = PriorityMap.get(nextToken.type);
                if (currentOpIndex != null && nextOpIndex != null && nextOpIndex < currentOpIndex) {
                    right = this.expr(true);
                }
                // When operators with different priorities are encountered in the loop, the recursion must be exited;
                if (inner && currentOpIndex != null && nextOpIndex != null && nextOpIndex > currentOpIndex) {
                    return new ast_1.BinaryOperatorNode(node, token, right || this.factor());
                }
            }
            node = new ast_1.BinaryOperatorNode(node, token, right || this.factor());
        }
        // 1 + (1 + 3) * 2
        return node;
    }
}
exports.FormulaExprParser = FormulaExprParser;
//# sourceMappingURL=parser.js.map